import React, { useState } from "react";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import { format } from "date-fns";

import type { ClientErrorRecord } from "src/interfaces/errorStats.interface";
import ErrorDetailsDialog from "./ErrorDetailsDialog";

const useStyles = makeStyles((theme) => ({
  table: {
    marginTop: theme.spacing(1),
  },
  messageCell: {
    maxWidth: 280,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  loadMore: {
    marginTop: theme.spacing(2),
  },
}));

function truncate(text: string, max = 80) {
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

function formatAccountant(error: ClientErrorRecord) {
  if (!error.accountant) return "—";
  return error.accountantUsername || error.accountant || "—";
}

interface ClientErrorsTableProps {
  errors: ClientErrorRecord[];
  showUser?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
}

function ClientErrorsTable({
  errors,
  showUser = false,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
}: ClientErrorsTableProps) {
  const classes = useStyles();
  const [selected, setSelected] = useState<ClientErrorRecord | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (!errors.length) {
    return (
      <Typography color="textSecondary">No client errors recorded.</Typography>
    );
  }

  return (
    <>
      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            {showUser ? <TableCell>User</TableCell> : null}
            <TableCell>Accountant</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Message</TableCell>
            <TableCell>Version</TableCell>
            <TableCell>Browser</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {errors.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell>
                {row.date
                  ? format(new Date(row.date), "MMM d, yyyy h:mm a")
                  : "—"}
              </TableCell>
              {showUser ? (
                <TableCell>
                  <Link to={`/support/user/${row.user}`}>{row.user}</Link>
                </TableCell>
              ) : null}
              <TableCell>{formatAccountant(row)}</TableCell>
              <TableCell>{row.source}</TableCell>
              <TableCell>{row.name || "—"}</TableCell>
              <TableCell className={classes.messageCell}>
                {truncate(row.message)}
              </TableCell>
              <TableCell>{row.clientVersion || "—"}</TableCell>
              <TableCell>{row.browserId || "—"}</TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => {
                    setSelected(row);
                    setDetailsOpen(true);
                  }}
                >
                  Details
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {hasMore && onLoadMore ? (
        <Box className={classes.loadMore}>
          <Button
            variant="outlined"
            color="primary"
            disabled={loadingMore}
            onClick={onLoadMore}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </Button>
        </Box>
      ) : null}

      <ErrorDetailsDialog
        error={selected}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        onExited={() => {
          setDetailsOpen((isOpen) => {
            if (!isOpen) setSelected(null);
            return isOpen;
          });
        }}
      />
    </>
  );
}

export default ClientErrorsTable;
